<template>
    <div class="clinics">
        <div v-for="clinic in filteredAndOrderedAppointments"
             :key="clinic.ClinicID"
             class="clinic"
        >
            <div class="clinic-header">
                <div v-if="clinic.ClinicID == videoClinicID" class="video-appointment-clinic-header hidden md:block">
                    <img src="../../assets/images/video-appointment.png" alt="" class="mx-auto" />
                    <h2 class="text-3xl leading-tight pb-3">Tips for your virtual appointment</h2>
                    <p class="text-base text-gray-darker pb-4">Make sure you’re in a quiet location with a good internet connection</p>
                    <p class="text-base text-gray-darker pb-4">Your device needs a camera and a microphone (you can test this before your appointment)</p>
                    <p class="text-base text-gray-darker pb-4">Any questions? Call us on <br> <a href="tel:03306780850" class="text-blue">0330 678 0850</a></p>
                </div>

                <div v-else>
                    <div class="clinic-title" :class="{'with-distance': clinic.Distance > 0.05}">
                        {{ clinic.ClinicName }} ({{ clinic.Postcode }})

                        <div class="clinic-distance-alt hidden md:block" v-if="clinic.Distance > 0.05">
                            {{ clinic.Distance | numFormat('0.0') }} miles
                        </div>
                    </div>

                    <div class="clinic-info md:hidden">
                        <div class="clinic-distance" v-if="clinic.Distance > 0.05">
                            {{ clinic.Distance | numFormat('0.0') }} miles
                        </div>

                        <div class="clinic-clinicians-link">
                            <a v-if="availableClinicians(clinic).length"
                            href="#"
                            :class="{open: cliniciansVisible(clinic)}"
                            @click.prevent="toggleClinicians(clinic)"
                            >
                                Available clinicians
                            </a>

                            <span v-else>Available clinicians</span>
                        </div>
                    </div>

                    <div class="clinic-clinicians md:hidden" v-if="cliniciansVisible(clinic)">
                        <button v-for="clinician in availableClinicians(clinic)"
                                type="button"
                                role="button"
                                :key="clinician.TherapistID"
                                class="btn btn-sm btn-secondary"
                                :class="clinicianClasses(clinic, clinician)"
                                @click.prevent="toggleClinician(clinic, clinician)"
                        >
                            {{ clinician.Name }}
                        </button>
                    </div>

                    <div class="clinic-clinicians hidden md:block" v-if="availableClinicians(clinic).length">
                        <div class="clinicians-title">Our clinicians</div>

                        <button v-for="clinician in availableClinicians(clinic)"
                                type="button"
                                role="button"
                                :key="clinician.TherapistID"
                                class="btn btn-sm btn-secondary"
                                :class="clinicianClasses(clinic, clinician)"
                                @click.prevent="toggleClinician(clinic, clinician)"
                        >
                            {{ clinician.Name }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="clinic-appointments">
                <div class="clinic-appointment-days" v-if="clinicHasAppointmentsInDateRange(clinic)">
                    <div v-for="(day, dayIndex) in days" :key="dayIndex" class="clinic-appointment-day">
                        <button v-for="(appointment, timeIndex) in getAppointmentsForDate(day, clinic)"
                                :key="timeIndex"
                                type="button"
                                role="button"
                                class="btn btn-sm btn-secondary"
                                :class="getAppointmentClasses(day, clinic, appointment)"
                                @click="setReservedAppointment({ clinic, appointment })"
                        >{{ appointment.Time.substr(0, appointment.Time.length - 3) }}</button>

                        <div class="w-full md:w-1/2">
                            <a v-if="shouldShowMoreSlotsMobile(day, clinic)"
                               href="#"
                               class="show-more block md:hidden"
                               @click.prevent="toggleAllSlots(day, clinic)"
                            >
                                <span v-if="shouldShowAllSlots(day, clinic)">Hide</span>
                                <span v-else>+{{ getAppointmentsForDate(day, clinic).length - 6 }} more</span>
                            </a>

                            <a v-if="shouldShowMoreSlotsDesktop(day, clinic)"
                               href="#"
                               class="show-more hidden md:block"
                               @click.prevent="toggleAllSlots(day, clinic)"
                            >

                                <span v-if="shouldShowAllSlots(day, clinic)">Hide</span>
                                <span v-else>+{{ getAppointmentsForDate(day, clinic).length - 10 }} more</span>
                            </a>
                        </div>
                    </div>
                </div>

                <div class="clinic-appointment-days display-only" v-else>
                    <div class="clinic-appointment-day"></div>
                    <div class="clinic-appointment-day"></div>
                    <div class="clinic-appointment-day"></div>
                    <div class="clinic-appointment-day"></div>
                    <div class="clinic-appointment-day"></div>

                    <next-available-appointments :clinic="clinic" />
                </div>
            </div>
        </div>

        <reserve-appointment
                v-if="reservedAppointment"
                :clinic="reservedAppointment.clinic"
                :therapist="reservedAppointment.therapist"
                :date="reservedAppointment.date"
                :time="reservedAppointment.time"
                :length="reservedAppointment.length"
                :close="clearReservedAppointment"
                modal-id="reserve-appointment"
         />
    </div>
</template>

<script>
    import { mapActions, mapState } from 'vuex';

    import NextAvailableAppointments from './NextAvailableAppointments';
    import ReserveAppointment from './ReserveAppointment';

    export default {
        name: 'clinics',

        components: {
            NextAvailableAppointments,
            ReserveAppointment,
        },

        props: {
            days: {
                type: Array,
                required: true,
            },
        },

        computed: {
            ...mapState('appointment', [
                'sort',
            ]),

            ...mapState('appointments', [
                'appointments',
                'reservedAppointment',
            ]),

            filteredAndOrderedAppointments() {
                return this.$store.getters['appointments/filteredAndOrderedAppointments'];
            },

            videoClinicID() {
                return process.env.VUE_APP_VIDEO_CLINIC_ID;
            }
        },

        data() {
            return {
                visibleClinicClinicians: [],
                hiddenClinicians: [],
                expandedDays: [],
                selectedClinicians: {},
            };
        },

        methods: {
            ...mapActions('appointments', [
                'setReservedAppointment',
                'clearReservedAppointment'
            ]),

            availableClinicians(clinic) {
                return this.$store.getters['appointments/availableClinicians'](clinic);
            },

            toggleClinicians(clinic) {
                let index = this.visibleClinicClinicians.indexOf(clinic.ClinicID);

                if (index === -1) {
                    this.visibleClinicClinicians.push(clinic.ClinicID);
                } else {
                    this.visibleClinicClinicians.splice(index, 1);
                }
            },

            cliniciansVisible(clinic) {
                return this.visibleClinicClinicians.indexOf(clinic.ClinicID) > -1;
            },

            clinicianClasses(clinic, clinician) {
                let classes = [`gender-${clinician.Gender.toLowerCase()}`];

                if (!this.clinicianVisible(clinic, clinician)) {
                    classes.push('opacity-25');
                }

                if (this.availableClinicians(clinic).length === 1) {
                    classes.push('only-clinician');
                }

                return classes;
            },

            toggleClinician(clinic, clinician) {
                if (this.selectedClinicians[clinic.ClinicID] === undefined) {
                    this.$set(this.selectedClinicians, clinic.ClinicID, clinician.TherapistID);
                } else if (this.selectedClinicians[clinic.ClinicID] === clinician.TherapistID) {
                    this.$delete(this.selectedClinicians, clinic.ClinicID);
                } else {
                    this.$set(this.selectedClinicians, clinic.ClinicID, clinician.TherapistID);
                }
            },

            clinicianSelected(clinic, clinician) {
                return this.selectedClinicians[clinic.ClinicID] !== undefined
                    && this.selectedClinicians[clinic.ClinicID] === clinician.TherapistID;
            },

            clinicianVisible(clinic, clinician) {
                return this.selectedClinicians[clinic.ClinicID] === undefined
                    || this.clinicianSelected(clinic, clinician);
            },

            clinicHasAppointmentsInDateRange(clinic) {
                let i, j, appointmentDate;

                for (i in clinic.Appointments) {
                    if (clinic.Appointments.hasOwnProperty(i)) {
                        appointmentDate = window.moment(clinic.Appointments[i].Date);

                        for (j in this.days) {
                            if (this.days.hasOwnProperty(j)) {
                                if (appointmentDate.isSame(this.days[j], 'day')) {
                                    return true;
                                }
                            }
                        }
                    }
                }

                return false;
            },

            getAppointmentsForDate(date, clinic) {
                return clinic.Appointments.filter(appointment => {
                    const appointmentDate = window.moment(appointment.Date);

                    return appointmentDate.isSame(date, 'day');
                });
            },

            shouldShowMoreSlotsMobile(date, clinic) {
                return this.getAppointmentsForDate(date, clinic).length > 6;
            },

            shouldShowMoreSlotsDesktop(date, clinic) {
                return this.getAppointmentsForDate(date, clinic).length > 10;
            },

            toggleAllSlots(date, clinic) {
                const id = `${clinic.ClinicID} ${date.format('X')}`;

                let index = this.expandedDays.indexOf(id);

                if (index === -1) {
                    this.expandedDays.push(id);
                } else {
                    this.expandedDays.splice(index, 1);
                }
            },

            shouldShowAllSlots(date, clinic) {
                const id = `${clinic.ClinicID} ${date.format('X')}`;

                return this.expandedDays.indexOf(id) > -1;
            },

            getAppointmentClasses(date, clinic, appointment) {
                let classes = [];

                if (!this.clinicianVisible(clinic, appointment.Therapist)) {
                    classes.push('opacity-25');
                }

                if (this.shouldShowAllSlots(date, clinic)) {
                    classes.push('visible');
                }

                return classes;
            },
        },
    }
</script>
